import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getPrice from '@custom/rtk-apis/price/getPrice';
import { CustomTable, Pagination, Search, Loader, ConfirmationPopup, SuccessModal } from '@ohif/ui';
import * as XLSX from 'xlsx';
import addInvestigation from '@custom/rtk-apis/billing/addInvestigation';
import getInvestigationApi from '@custom/rtk-apis/billing/getInvestigation';
import deleteInvestigationApi from '@custom/rtk-apis/billing/deleteInvestigation';
import { capitalizeWords, customStyle, getCurrencySymbol } from '@custom/Utils/utils';
import AddEditInvestigation from './addEditInvestigation';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { useNavigate } from 'react-router-dom';
import { servicesManager } from '../../app/src/App';
import { useTheme } from '../../ui/src/contextProviders';

interface InvestigationProps {
  activeTab: string;
  setActiveTab?: (tab: string) => void;
}

const Investigation = (props: InvestigationProps) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { uiNotificationService } = servicesManager.services;
  const { result: pricingData, loading } = useSelector((state: RootState) => state.getPriceSliceResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);
  const [tableData, setTableData] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(10);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loadingInvestigation, setLoadingInvestigation] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [msg, setMsg] = useState('');
  const [editData, setEditData] = useState(null);
  const [currency, setCurrency] = useState("");
  const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');

  useEffect(() => {
    dispatch(getPrice({ modality: "", studyRegion: "" }));
    dispatch(getBillingGeneralSettings());
  }, [dispatch]);

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData.result.currency);
    }
  }, [billingGeneralSettingsData]);

  useEffect(() => {
    if (currency) {
      fetchInvestigationData();
    }
  }, [currency, search, pgNumber, pgLimit]);

  const fetchInvestigationData = async () => {
    setLoadingInvestigation(true);
    try {
      const response = await dispatch(getInvestigationApi({ search: search, page: pgNumber, limit: pgLimit }));
      if (response.payload && response.payload.data) {
        const formattedData = response.payload.data.map(item => ({
          "Package Name": capitalizeWords(item["Package Name"]) || "",
          "Services Included": capitalizeWords(item["Services Included"]) || "",
          "Price": `${getCurrencySymbol(currency)}${item["Price"] || 0}`,
          "uniqueCode": item["uniqueCode"] || ""
        }));
        setTotalPg(response.payload.total);
        setTableData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching investigation data:", error);
    } finally {
      setLoadingInvestigation(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      alert("Invalid file format. Please upload an Excel file (.xls or .xlsx)");
      return;
    }

    setUploading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      setTimeout(async () => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          if (jsonData.length === 0) {
            alert("No data found in the uploaded file.");
          } else {
            setTableData(
              jsonData.map((item: Record<string, any>) => ({
                ...item,
                "Price": `${getCurrencySymbol(currency)}${item["Price"] ? parseFloat(item["Price"]) : 0}`,
              }))
            );

            const packageData = {
              hospitalId: hospitalId,
              packages: jsonData.map((item) => ({
                "Package Name": item["Package Name"] || "",
                "Services Included": item["Services Included"] || "",
                "Price": item["Price"] ? parseFloat(item["Price"]) : 0,
              })),
            };

            try {
              await dispatch(addInvestigation({ body: packageData }));
              await fetchInvestigationData();
              setSuccess(true);
              setMsg("File uploaded successfully.");
            } catch (error) {
              console.error("Error adding investigation:", error);
              alert(`Failed to add investigation: ${packageData.packageName}`);
            }
          }
        } catch (error) {
          console.error("Error processing file:", error);
          alert("Error processing file. Please check the file format and try again.");
        }
        setUploading(false);
      }, 2000);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDeleteClick = (item) => {
    setSelectedRow(item);
    setIsPopupOpen(true);
  };

  const handleConfirm = async () => {
    if (selectedRow) {
      await dispatch(deleteInvestigationApi({ id: selectedRow.uniqueCode }));
      setIsPopupOpen(false);
      await fetchInvestigationData();
    }
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const toggleAddForm = () => {
    if (billingGeneralSettingsData?.msg === "General settings not found for this hospital.") {
      props.setActiveTab?.('general');
      uiNotificationService.show({
        title: 'Billing Settings',
        message: 'Fill the general settings to create appointment.',
        type: 'error',
      });
      navigate('/billing');
    } else {
      setEditData(null);
      setShowAddForm(!showAddForm);
    }
  };

  const handleEditClick = (item) => {
    setEditData(item);
    setShowAddForm(true);
  };

  const refreshData = () => {
    fetchInvestigationData();
  };

  const actions = [
    {
      id: 'editPackage',
      label: 'Edit',
      iconName: 'edit',
      onClick: handleEditClick,
    },
    {
      id: 'deletePackage',
      label: 'Delete',
      iconName: 'delete',
      onClick: handleDeleteClick,
    },
  ];

  const columns = [
    { key: 'Package Name', title: 'Services Investigation' },
    { key: 'Services Included', title: 'Services Group' },
    { key: 'Price', title: 'Price' },
    { key: 'actions', title: 'Action' },
  ];

  const searchClick = (searchText) => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);
  };

  return (
    <>
      <div className='page__body' style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
        {showAddForm ? (
          <AddEditInvestigation
            setShowAddForm={setShowAddForm}
            investigationData={editData}
            onDataUpdated={refreshData}
          />
        ) : (
          <div className='tableWrap'>
            <div className='tableWrap__head'>
              <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search text="Services Investigation" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue} />
                </div>
              </div>
              <div className='btn__group'>
                <input type='file' accept='.xls,.xlsx' onChange={handleFileUpload} style={{ display: 'none' }} id='upload-input' />
                <label htmlFor='upload-input' className='btn btn__white'>Upload Investigation</label>
                <button className="btn btn__white" onClick={toggleAddForm}>
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Add Investigation
                </button>
              </div>
            </div>
            <div className='tableContainer'>
              <CustomTable columns={columns} data={tableData} actions={actions} from='investigation' />
            </div>
          </div>
        )}
      </div>
      <Loader isLoaderOpen={loading || uploading || loadingInvestigation} />
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to delete ${selectedRow?.["Package Name"]}?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title=""
      />
      <SuccessModal isSuccessModalOpen={success} title={msg || 'File uploaded successfully.'} handleCloseModal={() => setSuccess(false)} />
      {tableData.length > 0 && !showAddForm && (
        <Pagination onChangePage={setPgNumber} onChangePerPage={setPgLimit} totalPage={totalPg} currentPage={pgNumber} perPage={pgLimit} />
      )}
    </>
  );
};

export default Investigation;
